// Desk Dashboard book statistics — the pure half of the "My Book" page.
//
// The page shows one set of numbers over one set of rows. A service-type chip
// narrows the rows, and every figure above the table must then narrow with them
// (SEP-200: before this, the two performance cards read whole-book values while
// the table beside them showed a subset). Each function here takes the VISIBLE
// rows and gives the figures for exactly those rows.
//
// The server stamps each row with its own return over the selected window
// (`rangeReturnPct`) and the ARC band's return over the same window
// (`benchmarkRangeReturnPct`), from one calculation. Re-weighting them here is
// therefore the same arithmetic the server did, over fewer rows: a chip cannot
// make the card disagree with the table, and it needs no new request.
//
// THE WINDOW FIELDS ARE THE ONLY RETURN FIELDS THIS MODULE READS. The rows also
// carry `ytdPct` and `benchmarkYtdPct`, which are always the year to date
// whatever the chip. A figure that ignores the selected window looks the same as
// a figure that failed to update, which is the fault SEP-200 reports. The page
// shows one window at a time, so the two YTD-only fields feed nothing.

import {
    clientRiskRatingLabel,
    clientRiskRatingRank,
    type DeskBenchmarkRange,
    type DeskBookReturnSource,
    type DeskDashboardClientRow,
} from '@septima/contract';

/** AUM-weighted mean of (weight, value) pairs, with the count that contributed. */
function weightedCoverage(pairs: ReadonlyArray<readonly [number | null, number | null]>): {
    pct: number | null;
    covered: number;
} {
    let num = 0;
    let den = 0;
    let covered = 0;
    for (const [weight, value] of pairs) {
        if (weight == null || value == null || weight <= 0) continue;
        num += weight * value;
        den += weight;
        covered += 1;
    }
    return { pct: den > 0 ? num / den : null, covered };
}

export interface DeskBookStats {
    /** Clients in view. */
    clientCount: number;
    /** AUM-weighted return over the selected window, across the visible rows. */
    rangeReturnPct: number | null;
    /** AUM-weighted ARC benchmark return over the same window. */
    benchmarkRangeReturnPct: number | null;
    /**
     * The book return less the ARC return, over the same window. Null when
     * either side is null, because a difference from an absent number is not a
     * number.
     */
    relativeReturnPct: number | null;
    /** Visible clients that carry both a window figure and a weight. */
    coveredClients: number;
    /** Distinct households (family groups) of the visible clients. */
    householdCount: number;
    /** Visible clients that are in no household. */
    ungroupedCount: number;
    /** The value of the visible clients by risk profile, in ladder order. */
    riskSplit: RiskSplitRow[];
}

/** One risk profile of the book: its clients and their value. */
export interface RiskSplitRow {
    /**
     * The official name of the risk profile ("Moderately Low Risk"), or null when
     * the client record holds no risk rating.
     */
    riskRating: string | null;
    clientCount: number;
    valueGbp: number;
    /** The share of the value of the visible clients, in percent. */
    sharePct: number;
}

/**
 * The value of the rows by risk profile. Each group carries the official name
 * of the profile (SEP-318), and the groups sort in ladder order, from Low Risk
 * to High Risk. A client with no risk rating goes into one null group and is
 * not dropped, because the reader must see that the split does not cover the
 * whole book. The null group always sorts last.
 */
export function riskSplit(rows: readonly DeskDashboardClientRow[]): RiskSplitRow[] {
    const byRating = new Map<string | null, { clientCount: number; valueGbp: number }>();
    let total = 0;
    for (const row of rows) {
        const key = clientRiskRatingLabel(row.riskRating);
        const group = byRating.get(key) ?? { clientCount: 0, valueGbp: 0 };
        const value = row.aumGbp ?? 0;
        group.clientCount += 1;
        group.valueGbp += value;
        total += value;
        byRating.set(key, group);
    }
    return [...byRating.entries()]
        .map(([riskRating, g]) => ({
            riskRating,
            clientCount: g.clientCount,
            valueGbp: g.valueGbp,
            sharePct: total > 0 ? (g.valueGbp / total) * 100 : 0,
        }))
        .sort((a, b) => {
            if (a.riskRating === null) return 1;
            if (b.riskRating === null) return -1;
            const byRank = clientRiskRatingRank(a.riskRating) - clientRiskRatingRank(b.riskRating);
            return byRank !== 0 ? byRank : a.riskRating.localeCompare(b.riskRating);
        });
}

/** Every figure the cards show, computed from the visible rows alone. */
export function deskBookStats(rows: readonly DeskDashboardClientRow[]): DeskBookStats {
    let ungroupedCount = 0;
    const households = new Set<string>();
    for (const row of rows) {
        if (row.householdId) households.add(row.householdId);
        else ungroupedCount += 1;
    }
    const range = weightedCoverage(rows.map((r) => [r.aumGbp, r.rangeReturnPct]));
    const benchmark = weightedCoverage(rows.map((r) => [r.aumGbp, r.benchmarkRangeReturnPct]));
    return {
        clientCount: rows.length,
        rangeReturnPct: range.pct,
        benchmarkRangeReturnPct: benchmark.pct,
        relativeReturnPct:
            range.pct != null && benchmark.pct != null ? range.pct - benchmark.pct : null,
        coveredClients: range.covered,
        householdCount: households.size,
        ungroupedCount,
        riskSplit: riskSplit(rows),
    };
}

/**
 * How each source reads on the card.
 *
 * The three sources are three methodologies, so the card names the one that
 * answered. Two windows measured different ways must not look like one series.
 * A switch and not a keyed object: the source names are enum VALUES, and a
 * snake_case object key breaks the repository's key convention.
 */
function sourceLabel(source: DeskBookReturnSource): string {
    switch (source) {
        case 'modified_dietz':
            return 'Modified Dietz';
        case 'ytd_holdings':
            return 'Live holdings';
        case 'returns_export':
            return 'Month-end returns';
        case 'none':
            return '';
    }
}

export interface PerformanceDetailInput {
    /** The window chip, for example "6M". It stays on the card. */
    rangeLabel: string;
    stats: DeskBookStats;
    benchmarkRange: DeskBenchmarkRange | undefined;
    /** True while "Custom" is selected and one date is still missing. */
    customPending: boolean;
}

/**
 * The line under the Performance card.
 *
 * It carries the WINDOW, the SOURCE, the AS-AT date and the COVERAGE. The label
 * above it is the plain word "Performance" (SEP-200), so this line is the only
 * place a reader learns which window they are looking at and how old it is.
 * Every branch names the window first for that reason.
 */
export function performanceDetail(input: PerformanceDetailInput): string {
    const { rangeLabel, stats, benchmarkRange, customPending } = input;
    if (customPending) return `${rangeLabel} · Choose both dates`;
    // No response yet. Name the window and nothing else: a load must not read
    // as a refusal.
    if (benchmarkRange === undefined) return rangeLabel;
    if (stats.rangeReturnPct == null) {
        // The server's reason is null when the WHOLE book had a figure and the
        // chosen service type holds no client that carries one. The chip did
        // that, so the line says so.
        const reason = benchmarkRange.unavailableReason ?? 'Not available for the clients in view';
        return `${rangeLabel} · ${reason}`;
    }
    const parts = [rangeLabel];
    const label = sourceLabel(benchmarkRange.bookReturnSource);
    if (label) {
        parts.push(
            benchmarkRange.bookReturnAsOf ? `${label} to ${benchmarkRange.bookReturnAsOf}` : label,
        );
    }
    if (stats.coveredClients < stats.clientCount) {
        parts.push(`${stats.coveredClients} of ${stats.clientCount} clients`);
    }
    return parts.join(' · ');
}
